import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PhoneInput } from '../components/ui/phone-input';
import { 
  Users, Plus, Search, Building2, Mail, Phone, MapPin,
  ChevronRight, ChevronLeft, Clock, CheckCircle, XCircle, Loader2,
  Eye, Trash2, ArrowRight, ArrowLeft, TrendingUp, Target,
  Calendar, User, AlertCircle, Briefcase, Handshake, Settings,
  Landmark, Compass, MessageSquarePlus, FileText
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const LEAD_STAGES = [
  { value: 'Initial Engagement', label: 'Initial Engagement', color: 'bg-slate-100 text-slate-800 border-slate-300', bgColor: 'bg-slate-50' },
  { value: 'Initial Presentation', label: 'Initial Presentation', color: 'bg-blue-100 text-blue-800 border-blue-300', bgColor: 'bg-blue-50' },
  { value: 'Document Shared', label: 'Document Shared', color: 'bg-indigo-100 text-indigo-800 border-indigo-300', bgColor: 'bg-indigo-50' },
  { value: 'Contract Under Review', label: 'Contract Under Review', color: 'bg-amber-100 text-amber-800 border-amber-300', bgColor: 'bg-amber-50' },
  { value: 'Contract Signed', label: 'Contract Signed', color: 'bg-green-100 text-green-800 border-green-300', bgColor: 'bg-green-50' },
  { value: 'Converted', label: 'Converted', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', bgColor: 'bg-emerald-50' },
];

const LEAD_TYPES = [
  { value: 'Sales', label: 'Sales', icon: Briefcase },
  { value: 'Partnerships', label: 'Partnerships', icon: Handshake },
  { value: 'Operations', label: 'Operations', icon: Settings },
  { value: 'Funding', label: 'Funding', icon: Landmark },
  { value: 'Strategic', label: 'Strategic', icon: Compass },
];

const STAGE_COLORS = {
  'Initial Engagement': 'border-l-slate-400 bg-slate-50',
  'Initial Presentation': 'border-l-blue-400 bg-blue-50',
  'Document Shared': 'border-l-indigo-400 bg-indigo-50',
  'Contract Under Review': 'border-l-amber-400 bg-amber-50',
  'Contract Signed': 'border-l-green-400 bg-green-50',
  'Lost': 'border-l-red-400 bg-red-50',
};

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeView, setActiveView] = useState('timeline');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    lead_type: 'Sales',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    notes: '',
  });
  
  const [stageData, setStageData] = useState({
    new_stage: '',
    notes: '',
    responsible_person_id: '',
    responsible_person_name: '',
    follow_up_date: '',
    expected_completion_date: '',
  });
  
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteData, setNoteData] = useState({
    notes: '',
    responsible_person_id: '',
    responsible_person_name: '',
    follow_up_date: '',
    expected_completion_date: '',
  });

  const { getAuthHeaders, currentOffice, user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Check if user is admin (can delete leads)
  const isAdmin = user?.roles?.some(r => r.role === 'super_admin' || r.role === 'office_admin');
  
  // Check if user has access to leads (admin or has Marketing Manager badge)
  const hasMarketingBadge = user?.special_badges?.some(b => 
    b.badge_type === 'marketing_manager' || b.badge_type === 'Marketing Manager'
  );
  const hasLeadsAccess = isAdmin || hasMarketingBadge;
  
  // Ref to prevent duplicate fetches
  const fetchingRef = useRef(false);
  
  // Helper to get office ID consistently
  const getOfficeId = useCallback(() => {
    if (currentOffice?.id) return currentOffice.id;
    const storedOffice = localStorage.getItem('current_office');
    if (storedOffice) {
      try {
        return JSON.parse(storedOffice)?.id;
      } catch (e) {
        console.error('Error parsing stored office:', e);
      }
    }
    return null;
  }, [currentOffice?.id]);
  
  // Helper to get headers with office ID
  const getRequestHeaders = useCallback(() => {
    const headers = getAuthHeaders();
    const officeId = getOfficeId();
    if (officeId) {
      headers['X-Office-ID'] = officeId;
    }
    return headers;
  }, [getAuthHeaders, getOfficeId]);

  useEffect(() => {
    // If user doesn't have access, don't even try to fetch
    if (!hasLeadsAccess) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    
    const controller = new AbortController();
    
    const loadData = async () => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      
      setLoading(true);
      setAccessDenied(false);
      
      try {
        const headers = getRequestHeaders();

        let url = `${API_URL}/api/leads?`;
        if (filterType && filterType !== 'all') {
          url += `lead_type=${encodeURIComponent(filterType)}&`;
        }
        if (searchQuery) {
          url += `search=${encodeURIComponent(searchQuery)}&`;
        }

        // Fetch leads first
        let leadsRes;
        try {
          leadsRes = await fetch(url, { headers, signal: controller.signal });
        } catch (fetchErr) {
          if (fetchErr.name === 'AbortError') return;
          throw fetchErr;
        }
        
        if (controller.signal.aborted) return;
        
        if (leadsRes.status === 403) {
          setAccessDenied(true);
          setLoading(false);
          fetchingRef.current = false;
          toast.error('Access denied. Marketing Manager badge or Admin role required.');
          return;
        }
        
        if (leadsRes.ok) {
          const leadsData = await leadsRes.json();
          if (!controller.signal.aborted) {
            setLeads(leadsData);
          }
        } else {
          console.error('Failed to load leads:', leadsRes.status);
          toast.error(`Failed to load leads (${leadsRes.status})`);
          setLoading(false);
          fetchingRef.current = false;
          return;
        }
        
        if (controller.signal.aborted) return;
        
        // Fetch stats
        try {
          const statsRes = await fetch(`${API_URL}/api/leads/stats`, { headers, signal: controller.signal });
          if (statsRes.ok && !controller.signal.aborted) {
            const statsData = await statsRes.json();
            setStats(statsData);
          }
        } catch (e) {
          if (e.name !== 'AbortError') console.error('Error fetching stats:', e);
        }
        
        if (controller.signal.aborted) return;
        
        // Fetch team members
        try {
          const teamRes = await fetch(`${API_URL}/api/leads/team-members`, { headers, signal: controller.signal });
          if (teamRes.ok && !controller.signal.aborted) {
            const teamData = await teamRes.json();
            setTeamMembers(teamData);
          }
        } catch (e) {
          if (e.name !== 'AbortError') console.error('Error fetching team members:', e);
        }
        
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Handle "body already used" error (happens in React StrictMode)
        if (error.message?.includes('body') && error.message?.includes('used')) {
          console.log('Request was duplicated (StrictMode), ignoring...');
          return;
        }
        console.error('Error fetching leads:', error);
        // Don't show error toast if access was already denied
        if (!accessDenied) {
          toast.error('Failed to load leads');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          fetchingRef.current = false;
        }
      }
    };
    
    loadData();
    
    return () => {
      controller.abort();
      fetchingRef.current = false;
    };
  }, [currentOffice?.id, filterType, searchQuery, getRequestHeaders, API_URL, hasLeadsAccess]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setAccessDenied(false);
    
    try {
      const headers = getRequestHeaders();

      let url = `${API_URL}/api/leads?`;
      if (filterType && filterType !== 'all') {
        url += `lead_type=${encodeURIComponent(filterType)}&`;
      }
      if (searchQuery) {
        url += `search=${encodeURIComponent(searchQuery)}&`;
      }

      // Fetch leads first
      const leadsRes = await fetch(url, { headers });
      
      if (leadsRes.status === 403) {
        setAccessDenied(true);
        setLoading(false);
        toast.error('Access denied. Marketing Manager badge or Admin role required.');
        return;
      }
      
      if (leadsRes.ok) {
        const leadsData = await leadsRes.json();
        setLeads(leadsData);
      } else {
        console.error('Failed to load leads:', leadsRes.status);
        toast.error(`Failed to load leads (${leadsRes.status})`);
        setLoading(false);
        return;
      }
      
      // Fetch stats
      try {
        const statsRes = await fetch(`${API_URL}/api/leads/stats`, { headers });
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }
      } catch (e) {
        console.error('Error fetching stats:', e);
      }
      
      // Fetch team members
      try {
        const teamRes = await fetch(`${API_URL}/api/leads/team-members`, { headers });
        if (teamRes.ok) {
          const teamData = await teamRes.json();
          setTeamMembers(teamData);
        }
      } catch (e) {
        console.error('Error fetching team members:', e);
      }
      
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [currentOffice?.id, filterType, searchQuery, getAuthHeaders, API_URL]);

  const handleCreateLead = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!formData.name) {
      toast.error('Company/Institution name is required');
      return;
    }

    setSaving(true);
    
    try {
      const headers = getAuthHeaders();
      const officeId = getOfficeId();
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }
      
      // Validate we have required headers
      if (!headers['Authorization']) {
        toast.error('Session expired. Please log in again.');
        setSaving(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData),
      });

      // Handle response
      if (response.ok) {
        const data = await response.json();
        toast.success('Lead created successfully');
        setShowCreateModal(false);
        resetForm();
        setTimeout(() => fetchData(), 500);
      } else {
        // Try to get error message from response
        let errorMsg = `Failed to create lead (${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          // Response might not be JSON
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Create lead error:', error);
      toast.error('Failed to create lead. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStage = async () => {
    if (!stageData.new_stage) {
      toast.error('Please select a stage');
      return;
    }

    setSaving(true);
    
    const headers = getRequestHeaders();
    
    const payload = {
      new_stage: stageData.new_stage,
      notes: stageData.notes || null,
      responsible_person_id: stageData.responsible_person_id || null,
      responsible_person_name: stageData.responsible_person_name || null,
      follow_up_date: stageData.follow_up_date || null,
      expected_completion_date: stageData.expected_completion_date || null,
    };

    try {
      const response = await fetch(`${API_URL}/api/leads/${selectedLead.id}/stage`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = { detail: await response.text() || 'Unknown error' };
      }

      if (response.ok) {
        toast.success(`Lead moved to ${stageData.new_stage}`);
        setShowStageModal(false);
        setStageData({ new_stage: '', notes: '', responsible_person_id: '', responsible_person_name: '', follow_up_date: '', expected_completion_date: '' });
        // Refresh detail modal if open
        if (showDetailModal) {
          setSelectedLead(data);
        }
        setTimeout(() => fetchData(), 500);
      } else {
        toast.error(data.detail || 'Failed to update stage');
      }
    } catch (error) {
      console.error('Update stage error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteData.notes) {
      toast.error('Please enter a note');
      return;
    }

    setSaving(true);
    
    const headers = getRequestHeaders();
    
    const payload = {
      notes: noteData.notes,
      responsible_person_id: noteData.responsible_person_id || null,
      responsible_person_name: noteData.responsible_person_name || null,
      follow_up_date: noteData.follow_up_date || null,
      expected_completion_date: noteData.expected_completion_date || null,
    };

    try {
      const response = await fetch(`${API_URL}/api/leads/${selectedLead.id}/notes`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = { detail: await response.text() || 'Unknown error' };
      }

      if (response.ok) {
        toast.success('Note added successfully');
        setShowNoteModal(false);
        setNoteData({ notes: '', responsible_person_id: '', responsible_person_name: '', follow_up_date: '', expected_completion_date: '' });
        // Refresh detail modal if open
        if (showDetailModal) {
          setSelectedLead(data);
        }
        setTimeout(() => fetchData(), 500);
      } else {
        toast.error(data.detail || 'Failed to add note');
      }
    } catch (error) {
      console.error('Add note error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    const headers = getRequestHeaders();
    
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'DELETE',
        headers,
      });

      if (response.ok) {
        toast.success('Lead deleted');
        setTimeout(() => fetchData(), 500);
      } else {
        let errorMsg = 'Failed to delete lead';
        try {
          const data = await response.json();
          errorMsg = data.detail || errorMsg;
        } catch (e) {
          // Use default error
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Delete lead error:', error);
      toast.error('An error occurred. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      lead_type: 'Sales',
      contact_person: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      notes: '',
    });
  };

  const openStageModal = (lead, direction = null) => {
    setSelectedLead(lead);
    
    // Pre-select next or previous stage based on direction
    const currentIndex = LEAD_STAGES.findIndex(s => s.value === lead.current_stage);
    let suggestedStage = '';
    
    if (direction === 'forward' && currentIndex < LEAD_STAGES.length - 1) {
      suggestedStage = LEAD_STAGES[currentIndex + 1].value;
    } else if (direction === 'backward' && currentIndex > 0) {
      suggestedStage = LEAD_STAGES[currentIndex - 1].value;
    }
    
    setStageData({ 
      new_stage: suggestedStage, 
      notes: '', 
      responsible_person_id: lead.current_responsible_id || '',
      responsible_person_name: lead.current_responsible_name || '',
      follow_up_date: '', 
      expected_completion_date: '' 
    });
    setShowStageModal(true);
  };

  const openNoteModal = (lead) => {
    setSelectedLead(lead);
    setNoteData({ 
      notes: '', 
      responsible_person_id: lead.current_responsible_id || '',
      responsible_person_name: lead.current_responsible_name || '',
      follow_up_date: '', 
      expected_completion_date: '' 
    });
    setShowNoteModal(true);
  };

  const openDetailModal = (lead) => {
    setSelectedLead(lead);
    setShowDetailModal(true);
  };

  const getStageColor = (stage) => {
    const stageObj = LEAD_STAGES.find(s => s.value === stage);
    return stageObj?.color || 'bg-gray-100 text-gray-800';
  };

  const getLeadTypeIcon = (type) => {
    const typeObj = LEAD_TYPES.find(t => t.value === type);
    return typeObj?.icon || Building2;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Group leads by stage for kanban view
  const leadsByStage = LEAD_STAGES.reduce((acc, stage) => {
    acc[stage.value] = leads.filter(l => l.current_stage === stage.value);
    return acc;
  }, {});

  // Lost leads
  const lostLeads = leads.filter(l => l.current_stage === 'Lost');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Show access denied message
  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="p-4 bg-red-100 rounded-full mb-4">
          <XCircle className="h-12 w-12 text-red-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-500 max-w-md">
          You don't have permission to access Leads Management. 
          Please contact your administrator to get the "Marketing Manager" badge assigned.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="h-6 w-6 text-blue-600" />
            Leads Management
          </h1>
          <p className="text-gray-500 mt-1">Track and manage your sales pipeline</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-lead-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Lead
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-2xl font-bold">{stats.total_leads}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Signed</p>
                  <p className="text-2xl font-bold">{stats.won_leads}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Lost</p>
                  <p className="text-2xl font-bold">{stats.lost_leads}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Conversion</p>
                  <p className="text-2xl font-bold">{stats.conversion_rate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters & Views */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Lead Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {LEAD_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <Tabs value={activeView} onValueChange={setActiveView} className="w-auto">
          <TabsList>
            <TabsTrigger value="kanban">Kanban</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Kanban View */}
      {activeView === 'kanban' && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {LEAD_STAGES.map((stage, idx) => (
              <div key={stage.value} className="w-72 flex-shrink-0">
                <div className={`rounded-t-lg p-3 ${stage.color.split(' ')[0]} border-b-2 ${stage.color.split(' ')[2]}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm">{stage.label}</h3>
                    <Badge variant="secondary" className="text-xs">
                      {leadsByStage[stage.value]?.length || 0}
                    </Badge>
                  </div>
                </div>
                <div className={`${stage.bgColor} rounded-b-lg p-2 min-h-[400px] space-y-2`}>
                  <AnimatePresence>
                    {leadsByStage[stage.value]?.map((lead) => {
                      const TypeIcon = getLeadTypeIcon(lead.lead_type);
                      return (
                        <motion.div
                          key={lead.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="bg-white rounded-lg border shadow-sm p-3 cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => openDetailModal(lead)}
                          data-testid={`lead-card-${lead.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-sm line-clamp-1">{lead.name}</h4>
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <TypeIcon className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-500">{lead.lead_type}</span>
                          </div>
                          {lead.contact_person && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                              <User className="h-3 w-3" />
                              {lead.contact_person}
                            </p>
                          )}
                          {lead.current_responsible_name && (
                            <p className="text-xs text-blue-600 flex items-center gap-1 mb-1">
                              <User className="h-3 w-3" />
                              Assigned: {lead.current_responsible_name}
                            </p>
                          )}
                          {lead.current_follow_up_date && (
                            <p className="text-xs text-orange-600 flex items-center gap-1 mb-1">
                              <Calendar className="h-3 w-3" />
                              Follow-up: {formatDate(lead.current_follow_up_date)}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t">
                            <span className="text-xs text-gray-400">
                              {lead.days_in_pipeline}d in pipeline
                            </span>
                            <div className="flex gap-1">
                              {idx > 0 && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  onClick={(e) => { e.stopPropagation(); openStageModal(lead, 'backward'); }}
                                  title="Move backward"
                                >
                                  <ArrowLeft className="h-3 w-3" />
                                </Button>
                              )}
                              {idx < LEAD_STAGES.length - 1 && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  onClick={(e) => { e.stopPropagation(); openStageModal(lead, 'forward'); }}
                                  title="Move forward"
                                >
                                  <ArrowRight className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                  {(!leadsByStage[stage.value] || leadsByStage[stage.value].length === 0) && (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      No leads
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Lost column */}
            {lostLeads.length > 0 && (
              <div className="w-72 flex-shrink-0">
                <div className="rounded-t-lg p-3 bg-red-100 border-b-2 border-red-300">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm text-red-800">Lost</h3>
                    <Badge variant="secondary" className="text-xs bg-red-200">
                      {lostLeads.length}
                    </Badge>
                  </div>
                </div>
                <div className="bg-red-50 rounded-b-lg p-2 min-h-[400px] space-y-2">
                  {lostLeads.map((lead) => (
                    <div
                      key={lead.id}
                      className="bg-white rounded-lg border border-red-200 shadow-sm p-3 cursor-pointer hover:shadow-md transition-shadow opacity-75"
                      onClick={() => openDetailModal(lead)}
                    >
                      <h4 className="font-medium text-sm line-clamp-1">{lead.name}</h4>
                      <p className="text-xs text-red-600 mt-1">{lead.lost_reason || 'No reason provided'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timeline View */}
      {activeView === 'timeline' && (
        <div className="space-y-4">
          {leads.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Target className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>No leads found</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create your first lead
                </Button>
              </CardContent>
            </Card>
          ) : (
            leads.filter(l => l.current_stage !== 'Lost').map((lead) => {
              const TypeIcon = getLeadTypeIcon(lead.lead_type);
              return (
                <Card key={lead.id} className="overflow-hidden">
                  <div className="flex">
                    {/* Lead Info */}
                    <div className="w-1/3 p-4 border-r bg-gray-50">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold">{lead.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <TypeIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500">{lead.lead_type}</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {lead.current_stage !== 'Converted' ? (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => openDetailModal(lead)} title="View lead details">
                                <Eye className="h-4 w-4" />
                              </Button>
                              {isAdmin && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.id); }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </>
                          ) : (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => openDetailModal(lead)} title="View details (locked)">
                                <CheckCircle className="h-4 w-4 text-emerald-600" />
                              </Button>
                              {isAdmin && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.id); }}
                                  title="Delete lead"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {lead.contact_person && (
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {lead.contact_person}
                        </p>
                      )}
                      {lead.address && (
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />
                          {lead.address}
                        </p>
                      )}
                      <div className="mt-3 pt-3 border-t">
                        <Badge className={getStageColor(lead.current_stage)}>
                          {lead.current_stage}
                        </Badge>
                        <p className="text-xs text-gray-400 mt-2">{lead.days_in_pipeline} days in pipeline</p>
                      </div>
                    </div>
                    
                    {/* Timeline */}
                    <div className="flex-1 p-4">
                      <div className="flex items-center justify-between h-full">
                        {LEAD_STAGES.map((stage, idx) => {
                          const isCompleted = lead.stage_index > idx || (lead.stage_index === idx && lead.current_stage === stage.value);
                          const isCurrent = lead.current_stage === stage.value;
                          const historyEntry = lead.stage_history?.find(h => h.stage === stage.value);
                          
                          return (
                            <div key={stage.value} className="flex-1 flex flex-col items-center relative">
                              {/* Connector line */}
                              {idx > 0 && (
                                <div className={`absolute left-0 top-4 w-1/2 h-0.5 -translate-x-1/2 ${
                                  lead.stage_index >= idx ? 'bg-green-500' : 'bg-gray-200'
                                }`} />
                              )}
                              {idx < LEAD_STAGES.length - 1 && (
                                <div className={`absolute right-0 top-4 w-1/2 h-0.5 translate-x-1/2 ${
                                  lead.stage_index > idx ? 'bg-green-500' : 'bg-gray-200'
                                }`} />
                              )}
                              
                              {/* Stage dot */}
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                                isCurrent 
                                  ? 'bg-blue-500 text-white ring-4 ring-blue-100' 
                                  : isCompleted 
                                    ? 'bg-green-500 text-white'
                                    : 'bg-gray-200 text-gray-400'
                              }`}>
                                {isCompleted && !isCurrent ? (
                                  <CheckCircle className="h-4 w-4" />
                                ) : (
                                  <span className="text-xs font-bold">{idx + 1}</span>
                                )}
                              </div>
                              
                              {/* Stage label */}
                              <p className={`text-xs mt-2 text-center font-medium ${
                                isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                              }`}>
                                {stage.label}
                              </p>
                              
                              {/* Date if completed */}
                              {historyEntry && (
                                <p className="text-xs text-gray-400 mt-1">
                                  {formatDate(historyEntry.entered_at)}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Create Lead Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-600" />
              New Lead
            </DialogTitle>
            <DialogDescription>
              Add a new lead to your sales pipeline
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Company/Institution Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter company or institution name"
              />
            </div>
            
            <div>
              <Label>Lead Type *</Label>
              <Select value={formData.lead_type} onValueChange={(v) => setFormData({ ...formData, lead_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Contact Person Name</Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="Primary contact name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="contact@company.com"
                />
              </div>
              
              <div>
                <Label>Contact Phone</Label>
                <PhoneInput
                  value={formData.contact_phone}
                  onChange={(value) => setFormData({ ...formData, contact_phone: value || '' })}
                  placeholder="Enter phone number"
                  defaultCountry="TZ"
                  data-testid="lead-contact-phone"
                />
              </div>
            </div>
            
            <div>
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full address"
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional information about this lead..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={(e) => handleCreateLead(e)} disabled={saving} type="button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Stage Modal */}
      <Dialog open={showStageModal} onOpenChange={setShowStageModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-blue-600" />
              Update Stage
            </DialogTitle>
            <DialogDescription>
              {selectedLead?.name} - Current: {selectedLead?.current_stage}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Move to Stage *</Label>
              <Select value={stageData.new_stage} onValueChange={(v) => setStageData({ ...stageData, new_stage: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new stage" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STAGES.map(stage => (
                    <SelectItem key={stage.value} value={stage.value}>
                      {stage.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="Lost" className="text-red-600">
                    Mark as Lost
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Responsible Person</Label>
              <Select 
                value={stageData.responsible_person_id} 
                onValueChange={(v) => {
                  const member = teamMembers.find(m => m.id === v);
                  setStageData({ 
                    ...stageData, 
                    responsible_person_id: v,
                    responsible_person_name: member?.name || ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select responsible person" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Follow-up Date</Label>
                <Input
                  type="date"
                  value={stageData.follow_up_date}
                  onChange={(e) => setStageData({ ...stageData, follow_up_date: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Expected Completion</Label>
                <Input
                  type="date"
                  value={stageData.expected_completion_date}
                  onChange={(e) => setStageData({ ...stageData, expected_completion_date: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Notes {stageData.new_stage === 'Lost' && '(Reason for loss) *'}</Label>
              <Textarea
                value={stageData.notes}
                onChange={(e) => setStageData({ ...stageData, notes: e.target.value })}
                placeholder={stageData.new_stage === 'Lost' ? 'Why was this lead lost?' : 'Add notes about this stage change...'}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStageModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateStage} disabled={saving || !stageData.new_stage}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              Update Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Modal with Timeline */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              {selectedLead?.name}
            </DialogTitle>
            <DialogDescription>
              <Badge className={getStageColor(selectedLead?.current_stage)}>
                {selectedLead?.current_stage}
              </Badge>
              <span className="ml-2 text-gray-500">
                {selectedLead?.days_in_pipeline} days in pipeline
              </span>
            </DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-6 py-4">
              {/* Lead Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 uppercase">Type</p>
                  <p className="font-medium flex items-center gap-2">
                    {React.createElement(getLeadTypeIcon(selectedLead.lead_type), { className: "h-4 w-4 text-gray-400" })}
                    {selectedLead.lead_type}
                  </p>
                </div>
                {selectedLead.contact_person && (
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 uppercase">Contact Person</p>
                    <p className="font-medium flex items-center gap-1">
                      <User className="h-4 w-4 text-gray-400" />
                      {selectedLead.contact_person}
                    </p>
                  </div>
                )}
                {selectedLead.contact_email && (
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 uppercase">Email</p>
                    <p className="font-medium flex items-center gap-1">
                      <Mail className="h-4 w-4 text-gray-400" />
                      {selectedLead.contact_email}
                    </p>
                  </div>
                )}
                {selectedLead.contact_phone && (
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 uppercase">Phone</p>
                    <p className="font-medium flex items-center gap-1">
                      <Phone className="h-4 w-4 text-gray-400" />
                      {selectedLead.contact_phone}
                    </p>
                  </div>
                )}
                {selectedLead.address && (
                  <div className="space-y-1 col-span-2">
                    <p className="text-xs text-gray-500 uppercase">Address</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      {selectedLead.address}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Current Stage Info */}
              {(selectedLead.current_responsible_name || selectedLead.current_follow_up_date || selectedLead.current_expected_completion) && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-600 uppercase font-semibold mb-2">Current Stage Details</p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    {selectedLead.current_responsible_name && (
                      <div>
                        <p className="text-gray-500 text-xs">Responsible</p>
                        <p className="font-medium">{selectedLead.current_responsible_name}</p>
                      </div>
                    )}
                    {selectedLead.current_follow_up_date && (
                      <div>
                        <p className="text-gray-500 text-xs">Follow-up</p>
                        <p className="font-medium">{formatDate(selectedLead.current_follow_up_date)}</p>
                      </div>
                    )}
                    {selectedLead.current_expected_completion && (
                      <div>
                        <p className="text-gray-500 text-xs">Expected Completion</p>
                        <p className="font-medium">{formatDate(selectedLead.current_expected_completion)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {selectedLead.notes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-1">Notes</p>
                  <p className="text-sm">{selectedLead.notes}</p>
                </div>
              )}
              
              {/* Stage Timeline */}
              <div>
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Stage Timeline
                </h4>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  
                  {/* Timeline items */}
                  <div className="space-y-4">
                    {selectedLead.stage_history?.map((history, idx) => {
                      const isCurrentStage = history.stage === selectedLead.current_stage;
                      const isNote = history.is_stage_change === false;
                      
                      return (
                        <div key={idx} className="relative flex gap-4 pl-10">
                          {/* Timeline dot */}
                          <div className={`absolute left-2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isNote
                              ? 'bg-yellow-100 border-yellow-400'
                              : isCurrentStage 
                                ? 'bg-blue-500 border-blue-500' 
                                : history.stage === 'Lost'
                                  ? 'bg-red-500 border-red-500'
                                  : history.stage === 'Contract Signed'
                                    ? 'bg-green-500 border-green-500'
                                    : 'bg-white border-green-500'
                          }`}>
                            {isNote ? (
                              <FileText className="h-3 w-3 text-yellow-600" />
                            ) : (history.stage === 'Contract Signed' || history.stage === 'Lost') && (
                              history.stage === 'Contract Signed' 
                                ? <CheckCircle className="h-3 w-3 text-white" />
                                : <XCircle className="h-3 w-3 text-white" />
                            )}
                          </div>
                          
                          {/* Content */}
                          <div className={`flex-1 p-3 rounded-lg border-l-4 ${
                            isNote 
                              ? 'border-l-yellow-400 bg-yellow-50' 
                              : STAGE_COLORS[history.stage] || 'border-l-gray-300 bg-gray-50'
                          }`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">
                                {isNote ? (
                                  <span className="flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    Note - {history.stage}
                                  </span>
                                ) : history.stage}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(history.entered_at)}
                              </span>
                            </div>
                            {history.responsible_person_name && (
                              <p className="text-xs text-blue-600 mb-1">
                                Responsible: {history.responsible_person_name}
                              </p>
                            )}
                            {history.follow_up_date && (
                              <p className="text-xs text-orange-600 mb-1">
                                Follow-up: {formatDate(history.follow_up_date)}
                              </p>
                            )}
                            {history.expected_completion_date && (
                              <p className="text-xs text-purple-600 mb-1">
                                Expected: {formatDate(history.expected_completion_date)}
                              </p>
                            )}
                            {history.notes && (
                              <p className="text-xs text-gray-600 mt-1">{history.notes}</p>
                            )}
                            {history.updated_by_name && (
                              <p className="text-xs text-gray-400 mt-1">
                                by {history.updated_by_name}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
            {isAdmin && (
              <Button 
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => { 
                  setShowDetailModal(false);
                  handleDeleteLead(selectedLead?.id);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
            {/* Show action buttons only if not converted */}
            {selectedLead?.current_stage !== 'Converted' && selectedLead?.current_stage !== 'Lost' && (
              <>
                <Button 
                  variant="secondary"
                  onClick={() => { setShowDetailModal(false); openNoteModal(selectedLead); }}
                >
                  <MessageSquarePlus className="h-4 w-4 mr-2" />
                  Add Note
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => { 
                    setShowDetailModal(false); 
                    setStageData({ ...stageData, new_stage: 'Lost' });
                    setShowStageModal(true);
                  }}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Mark as Lost
                </Button>
                {/* Show Mark as Converted button when at Contract Signed stage */}
                {selectedLead?.current_stage === 'Contract Signed' ? (
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => { 
                      setShowDetailModal(false); 
                      setStageData({ ...stageData, new_stage: 'Converted', notes: 'Lead fully converted and locked.' });
                      setShowStageModal(true);
                    }}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark as Converted
                  </Button>
                ) : (
                  <Button onClick={() => { setShowDetailModal(false); openStageModal(selectedLead); }}>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Update Stage
                  </Button>
                )}
              </>
            )}
            {/* Show locked message for converted leads */}
            {selectedLead?.current_stage === 'Converted' && (
              <div className="flex items-center gap-2 text-emerald-600 text-sm">
                <CheckCircle className="h-4 w-4" />
                This lead is fully converted and locked
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="h-5 w-5 text-yellow-600" />
              Add Note / Update
            </DialogTitle>
            <DialogDescription>
              {selectedLead?.name} - Stage: {selectedLead?.current_stage}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Note / Update *</Label>
              <Textarea
                value={noteData.notes}
                onChange={(e) => setNoteData({ ...noteData, notes: e.target.value })}
                placeholder="Enter your note or update about this lead..."
                rows={4}
              />
            </div>
            
            <div>
              <Label>Responsible Person</Label>
              <Select 
                value={noteData.responsible_person_id} 
                onValueChange={(v) => {
                  const member = teamMembers.find(m => m.id === v);
                  setNoteData({ 
                    ...noteData, 
                    responsible_person_id: v,
                    responsible_person_name: member?.name || ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select responsible person" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Follow-up Date</Label>
                <Input
                  type="date"
                  value={noteData.follow_up_date}
                  onChange={(e) => setNoteData({ ...noteData, follow_up_date: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Expected Completion</Label>
                <Input
                  type="date"
                  value={noteData.expected_completion_date}
                  onChange={(e) => setNoteData({ ...noteData, expected_completion_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteModal(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={saving || !noteData.notes}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageSquarePlus className="h-4 w-4 mr-2" />}
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
